import { useCallback, useEffect, useMemo, useRef } from 'react'
import { useSelector } from 'react-redux'

import { skipToken } from '@reduxjs/toolkit/query'

import { isV2AiConversation } from '@lyrahealth-inc/shared-app-logic/src/features/messaging/conversations'
import { CONVERSATION_TYPE } from '@lyrahealth-inc/shared-app-logic/src/models/messages/Messages'

import {
  useGetMessagesQuery,
  useGetMessagesV2Query,
  useLazyGetMessagesQuery,
  useLazyGetMessagesV2Query,
} from '../messagesApi'
import { useSelectedConversation } from './useSelectedConversation'
import { getAuthUserId } from '../../../../data/auth/authSelectors'

export const useInfiniteMessages = () => {
  const providerId = useSelector(getAuthUserId)
  const selectedConversation = useSelectedConversation()
  const shouldUseV2 = isV2AiConversation(selectedConversation)
  const conversationArgs =
    selectedConversation?.conversation_id && providerId
      ? { conversationId: selectedConversation.conversation_id, providerId, cursor: undefined }
      : skipToken
  const v1Result = useGetMessagesQuery(!shouldUseV2 ? conversationArgs : skipToken, {
    refetchOnMountOrArgChange: true,
  })
  const v2Result = useGetMessagesV2Query(shouldUseV2 ? conversationArgs : skipToken, {
    refetchOnMountOrArgChange: true,
  })
  const { data, currentData, isFetching, isLoading, isSuccess } = shouldUseV2 ? v2Result : v1Result
  const isProviderPair = selectedConversation?.conversation_attributes?.type === CONVERSATION_TYPE.PROVIDER_PROVIDER
  const providerPairId = isProviderPair ? selectedConversation?.conversation_id : undefined
  // A pair is fresh once a fetch that started after it opened has finished. Tracking the fetch
  // instead of fulfilledTimeStamp keeps this independent of Date.now().
  const openingHistoryRef = useRef({ providerPairId, providerId, sawRefresh: isFetching, isReady: false })
  const openingHistory = openingHistoryRef.current
  const hasFreshPairHistory =
    !!currentData &&
    openingHistory.providerPairId === providerPairId &&
    openingHistory.providerId === providerId &&
    (openingHistory.isReady || (openingHistory.sawRefresh && !isFetching))
  useEffect(() => {
    if (
      openingHistoryRef.current.providerPairId !== providerPairId ||
      openingHistoryRef.current.providerId !== providerId
    ) {
      openingHistoryRef.current = { providerPairId, providerId, sawRefresh: isFetching, isReady: false }
      return
    }
    openingHistoryRef.current.sawRefresh ||= isFetching
    openingHistoryRef.current.isReady ||= hasFreshPairHistory && isSuccess
  }, [providerPairId, providerId, isFetching, hasFreshPairHistory, isSuccess])

  const [triggerV1] = useLazyGetMessagesQuery()
  const [triggerV2] = useLazyGetMessagesV2Query()

  const history = shouldUseV2 ? data : currentData
  const loadMore = useCallback(() => {
    if (!isFetching && history?.next_page_token && selectedConversation?.conversation_id && providerId) {
      const args = {
        conversationId: selectedConversation.conversation_id,
        providerId,
        cursor: history.next_page_token,
      }
      if (shouldUseV2) {
        triggerV2(args)
      } else {
        triggerV1(args)
      }
    }
  }, [
    history?.next_page_token,
    isFetching,
    providerId,
    selectedConversation?.conversation_id,
    shouldUseV2,
    triggerV1,
    triggerV2,
  ])

  const messages = useMemo(() => {
    if (!history?.conversation_messages) {
      return []
    }
    return [...history.conversation_messages].reverse()
  }, [history?.conversation_messages])

  return {
    data: messages,
    loadMore,
    hasMore: !!history?.next_page_token && !!selectedConversation?.conversation_id,
    isLoadingNext: isFetching && isSuccess,
    isLoading: shouldUseV2 ? isLoading : isFetching && !currentData,
    // Pair reads wait for the opening refresh; client and AI keep their existing readiness.
    // Later pagination must not reset the pair's opening readiness.
    isSuccess: isSuccess && (!isProviderPair || hasFreshPairHistory),
  }
}
